import { sha512Hex } from './PendingOperationTerminalization';

export const CANCELLATION_SCHEMA = 'checklist-create-delete-cancellation/v1';
const PARENT = 'guincho_checklists';
const VALUES = 'guincho_valores_checklist';
export interface CancellationOperation {
  id: string; operation: string; collection: string; item_id: string;
  data?: any; timestamp: number; user_id?: string | null; empresa_id?: string | null;
}
export interface CancellationManifest {
  schema: typeof CANCELLATION_SCHEMA; companyId: string; checklistId: string;
  operations: CancellationOperation[]; eligible: boolean;
}
export interface CancellationRow {
  id: string; empresa_id: string; item_id: string; payload: string; payload_sha512: string;
  status: string; reason: string;
}
export interface CancellationDatabase {
  listItems<T>(table: string, query?: any): Promise<T[]>;
  readItem<T>(table: string, id: string): Promise<T | null>;
  createItem(table: string, row: any, skip: boolean): Promise<any>;
  updateItemIfUnchanged(table: string, id: string, updates: any, expected: any, skip: boolean): Promise<boolean>;
  removePendingOperationIfUnchanged(id: string, expected: any, guard?: () => boolean): Promise<boolean>;
}
export const cancellationPayload = (op: { data?: any }): any => {
  try { return typeof op.data === 'string' ? JSON.parse(op.data) : op.data ?? {}; }
  catch { return {}; }
};
const belongs = (op: CancellationOperation, company: string) => {
  const evidence = [op.empresa_id, cancellationPayload(op)?.empresa_id].filter(Boolean);
  return evidence.length > 0 && evidence.every(value => value === company);
};
const related = (op: CancellationOperation, id: string) =>
  (op.collection === PARENT && op.item_id === id) || (op.collection === VALUES &&
    (cancellationPayload(op)?.checklist_id === id ||
      (op.operation === 'checklist_partial_update' && op.item_id === id)));

export const readCancellationManifest = (row: CancellationRow): CancellationManifest => {
  if (sha512Hex(row.payload) !== row.payload_sha512) throw new Error('Arquivo do cancelamento divergiu.');
  const manifest = JSON.parse(row.payload) as CancellationManifest;
  if (manifest.schema !== CANCELLATION_SCHEMA || manifest.companyId !== row.empresa_id ||
      manifest.checklistId !== row.item_id) throw new Error('Escopo do cancelamento divergiu.');
  return manifest;
};
export const listChecklistCancellations = async (db: CancellationDatabase, company: string) =>
  (await db.listItems<CancellationRow>('sync_conflicts', { empresa_id: company }))
    .filter(row => row.empresa_id === company && row.reason === CANCELLATION_SCHEMA);

/** O arquivo integral precede qualquer bloqueio/remoção e não atribui autoria ao leitor. */
export const discoverChecklistCancellations = async (db: CancellationDatabase, company: string) => {
  const queue = await db.listItems<CancellationOperation>('pending_operations');
  for (const create of queue.filter(op => op.collection === PARENT && op.operation === 'create' && belongs(op, company))) {
    const payload = cancellationPayload(create);
    if (payload?.id !== create.item_id) continue;
    const group = queue.filter(op => related(op, create.item_id));
    const deletes = group.filter(op => op.collection === PARENT && op.operation === 'delete' &&
      op.timestamp > create.timestamp &&
      cancellationPayload(op)?.id === create.item_id);
    if (!deletes.length) continue;
    const deleted = deletes[0];
    const eligible = deletes.length === 1 && group.filter(op => op.collection === PARENT && op.operation === 'create').length === 1 &&
      group.every(op => belongs(op, company) && op.timestamp <= deleted.timestamp &&
        (op.collection === PARENT ? ['create', 'delete'].includes(op.operation) :
          cancellationPayload(op)?.checklist_id === create.item_id &&
          (!cancellationPayload(op)?.id || cancellationPayload(op).id === op.item_id))) &&
      !await db.readItem(PARENT, create.item_id);
    const existing = await listChecklistCancellations(db, company);
    if (existing.some(row => row.item_id === create.item_id)) continue;
    const manifest: CancellationManifest = { schema: CANCELLATION_SCHEMA, companyId: company,
      checklistId: create.item_id, eligible, operations: group.sort((a, b) => a.timestamp - b.timestamp || a.id.localeCompare(b.id)) };
    const serialized = JSON.stringify(manifest);
    const digest = sha512Hex(serialized);
    const now = Date.now();
    await db.createItem('sync_conflicts', { id: `cancel:${digest}`, empresa_id: company,
      user_id: null, collection: PARENT, item_id: create.item_id, reason: CANCELLATION_SCHEMA,
      payload: serialized, payload_sha512: digest, status: 'cancellation_pending', created_at: now, updated_at: now }, true);
  }
  const rows = await listChecklistCancellations(db, company);
  for (const row of rows.filter(item => item.status === 'cancellation_remote_exists')) {
    const manifest = readCancellationManifest(row);
    if (manifest.operations.some(op => queue.some(pending => pending.id === op.id))) continue;
    const tombstones = await db.listItems<any>('sync_tombstones', { empresa_id: company });
    if (tombstones.some(t => t.empresa_id === company && t.entity_type === PARENT && t.entity_id === row.item_id)) {
      await db.updateItemIfUnchanged('sync_conflicts', row.id, { status: 'resolved', resolved_at: Date.now(), updated_at: Date.now() },
        { payload: row.payload, status: 'cancellation_remote_exists' }, true);
    }
  }
  return listChecklistCancellations(db, company);
};

/** Remoto existente libera somente a exclusão original; nunca uma recriação. */
export const cancellationBlocksOperation = (rows: CancellationRow[], op: CancellationOperation): boolean =>
  rows.some(row => {
    const manifest = readCancellationManifest(row);
    return related(op, manifest.checklistId) && !(row.status === 'cancellation_remote_exists' && op.operation === 'delete');
  });

export interface CancellationReceipt {
  result: 'cancelled' | 'remote_exists'; company_id: string; checklist_id: string;
  manifest_sha512: string; actor_id: string | null; sequence?: number;
}
export const authorizeChecklistCancellation = async (input: {
  db: CancellationDatabase; row: CancellationRow;
  current: () => boolean; request: (manifest: string, digest: string) => Promise<CancellationReceipt>;
}): Promise<CancellationReceipt> => {
  const { db, row } = input;
  const manifest = readCancellationManifest(row);
  if (!manifest.eligible) throw new Error('Par ambíguo preservado; revisão do suporte necessária.');
  const currentRow = await db.readItem<CancellationRow>('sync_conflicts', row.id);
  if (!currentRow || currentRow.payload !== row.payload || !input.current()) throw new Error('Sessão ou seleção mudou.');
  // Após ACK, o manifesto integral é o arquivo terminal e permite retomar CAS parcial.
  if (currentRow.status !== 'cancellation_acknowledged' && currentRow.status !== 'resolved') {
    if (await db.readItem(PARENT, manifest.checklistId)) throw new Error('Checklist reapareceu localmente; revisão necessária.');
    const queue = await db.listItems<CancellationOperation>('pending_operations');
    const captured = new Map(manifest.operations.map(op => [op.id, JSON.stringify(op)]));
    if (queue.filter(op => related(op, manifest.checklistId)).length !== captured.size ||
      manifest.operations.some(op => JSON.stringify(queue.find(row => row.id === op.id)) !== captured.get(op.id))) {
      throw new Error('Fila mudou; cancelamento não autorizado.');
    }
  }
  if (!input.current()) throw new Error('Sessão mudou.');
  const receipt = await input.request(row.payload, row.payload_sha512);
  if (!input.current()) throw new Error('Sessão mudou; confirmação será retomada.');
  if (receipt.company_id !== manifest.companyId || receipt.checklist_id !== manifest.checklistId ||
      receipt.manifest_sha512 !== row.payload_sha512 ||
      (receipt.actor_id !== null && typeof receipt.actor_id !== 'string') ||
      !['cancelled', 'remote_exists'].includes(receipt.result) ||
      (receipt.result === 'cancelled' && !receipt.sequence)) throw new Error('Confirmação remota divergente.');
  const status = receipt.result === 'cancelled' ? 'cancellation_acknowledged' : 'cancellation_remote_exists';
  if (!await db.updateItemIfUnchanged('sync_conflicts', row.id,
    { status, recovery_id: JSON.stringify(receipt), updated_at: Date.now() },
    { payload: row.payload, payload_sha512: row.payload_sha512 }, true)) throw new Error('Arquivo mudou.');
  if (receipt.result === 'remote_exists') return receipt;
  for (const operation of manifest.operations) {
    if (!input.current()) throw new Error('Sessão mudou; arquivo e barreira preservados.');
    if (!await db.readItem('pending_operations', operation.id)) continue;
    const { id, ...expected } = operation;
    if (!await db.removePendingOperationIfUnchanged(id, expected, input.current)) {
      throw new Error('Pendência mudou; arquivo e barreira preservados.');
    }
  }
  await db.updateItemIfUnchanged('sync_conflicts', row.id, { status: 'resolved', resolved_at: Date.now(), updated_at: Date.now() },
    { payload: row.payload, status: 'cancellation_acknowledged' }, true);
  return receipt;
};

/** Processa pares exatos da empresa; um par ambíguo permanece no arquivo para suporte. */
export const processEligibleChecklistCancellations = async (input: {
  db: CancellationDatabase;
  companyId: string;
  current: () => boolean;
  request: (manifest: string, digest: string) => Promise<CancellationReceipt>;
}): Promise<{ cancelled: number; remoteExists: number; failed: number }> => {
  const result = { cancelled: 0, remoteExists: 0, failed: 0 };
  const rows = await discoverChecklistCancellations(input.db, input.companyId);
  for (const row of rows) {
    if (!input.current()) throw new Error('Sessão mudou durante cancelamentos de checklist.');
    if (row.status === 'resolved' || row.status === 'cancellation_remote_exists') continue;
    try {
      if (!readCancellationManifest(row).eligible) continue;
      const receipt = await authorizeChecklistCancellation({
        db: input.db,
        row,
        current: input.current,
        request: input.request,
      });
      if (receipt.result === 'cancelled') result.cancelled += 1;
      else result.remoteExists += 1;
    } catch (error) {
      if (!input.current()) throw error;
      result.failed += 1;
    }
  }
  return result;
};
